import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Wallet, TrendingUp, Award, Gift, ArrowUpRight, Copy, Users2,
  ShieldCheck, Newspaper, CircleDollarSign, Landmark, Lock, Eye, EyeOff, Loader2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import OffersTicker from "@/components/OffersTicker";
import BannerCarousel from "@/components/BannerCarousel";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "---";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "---"; }
}

function InfoCell({ label, value, color, testId }) {
  return (
    <div className={`rounded-xl p-3 border ${color}`} data-testid={testId}>
      <div className="text-[11px] uppercase font-semibold text-slate-500 tracking-wide">{label}</div>
      <div className="mt-1 text-sm font-bold text-slate-900 truncate">{value}</div>
    </div>
  );
}

function GradientCard({ gradient, icon: Icon, label, value, testId, sub, onClick }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      data-testid={testId}
      onClick={onClick}
      className={`relative overflow-hidden rounded-xl p-5 shadow-lg group hover:-translate-y-0.5 transition-transform text-left w-full ${gradient} ${
        onClick ? "cursor-pointer active:scale-[0.99]" : ""
      }`}
    >
      <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/15 rounded-full blur-2xl group-hover:bg-white/25 transition-colors" />
      <div className="absolute inset-0 border border-white/20 rounded-xl pointer-events-none" />
      <div className="relative z-10 flex items-center gap-2 text-white/90">
        <Icon size={16} />
        <span className="text-[11px] uppercase font-bold tracking-[0.15em]">{label}</span>
      </div>
      <div className="relative z-10 mt-2 text-2xl sm:text-3xl font-extrabold text-white tracking-tight"
           style={{ fontFamily: "Outfit, sans-serif" }}>
        {value}
      </div>
      {sub && <div className="relative z-10 text-[11px] text-white/80 mt-1">{sub}</div>}
    </Comp>
  );
}

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [banners, setBanners] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  const refLink = `${window.location.origin}/signup?ref=${user?.user_code || ""}`;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [d, b, n] = await Promise.all([
          api.get("/dashboard"),
          api.get("/banners"),
          api.get("/news"),
        ]);
        if (!mounted) return;
        setData(d.data);
        setBanners(b.data.banners);
        setNews(n.data.news);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(refLink);
      toast.success("Referral link copied!");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header />
        <div className="p-6 flex items-center justify-center">
          <div className="h-10 w-10 rounded-full border-4 border-red-600 border-t-transparent animate-spin" />
        </div>
      </div>
    );
  }

  const u = data.user;
  const team = data.team;
  const w = data.wallet;

  return (
    <div className="min-h-screen bg-slate-100">
      <Header />
      <OffersTicker />

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6 pb-16">
        {/* Banner */}
        <BannerCarousel banners={banners} />

        {/* User Info Card */}
        <section data-testid="user-info-card" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6">
          <h2 className="text-center text-red-700 font-bold text-lg mb-4"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            Welcome back, {u.name}!
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <InfoCell label="User Name" value={u.name} color="bg-slate-50 border-slate-200" testId="info-username" />
            <InfoCell label="User ID" value={u.user_code} color="bg-blue-50 border-blue-200" testId="info-userid" />
            <InfoCell label="User Rank" value={u.rank} color="bg-amber-50 border-amber-200" testId="info-rank" />
            <InfoCell label="Sponsor Name" value={u.sponsor_name || "---"} color="bg-emerald-50 border-emerald-200" testId="info-sponsor-name" />
            <InfoCell label="Sponsor ID" value={u.sponsor_user_code || "---"} color="bg-cyan-50 border-cyan-200" testId="info-sponsor-id" />
            <InfoCell label="Activation Date" value={formatDate(u.activation_date)} color="bg-rose-50 border-rose-200" testId="info-activation" />
          </div>

          <div
            data-testid="team-stats-banner"
            className="mt-4 rounded-xl p-4 text-white shadow-md relative overflow-hidden"
            style={{ backgroundImage: "linear-gradient(90deg, #f97316 0%, #dc2626 100%)" }}
          >
            <div className="absolute -right-8 -top-8 w-28 h-28 bg-white/15 rounded-full blur-2xl" />
            <div className="relative flex items-center gap-2 font-semibold">
              <Users2 size={18} /> Total Team - <span data-testid="stat-total-team">{team.total}</span>
            </div>
            <div className="relative flex items-center gap-2 font-semibold mt-1">
              <TrendingUp size={18} /> Active Team - <span data-testid="stat-active-team">{team.active}</span>
            </div>
          </div>

          <div className="mt-4 flex justify-center">
            <span
              data-testid="account-status-badge"
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold border ${
                u.activated
                  ? "text-emerald-700 bg-emerald-50 border-emerald-300"
                  : "text-slate-600 bg-slate-100 border-slate-300"
              }`}
            >
              <ShieldCheck size={14} /> {u.activated ? "Account Active" : "Inactive"}
            </span>
          </div>
        </section>

        {/* Referral Link */}
        <section
          data-testid="referral-section"
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5"
        >
          <h3 className="text-center text-red-700 font-bold mb-3 flex items-center justify-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            <Users2 size={18} /> Referral Program
          </h3>
          <p className="text-xs text-center text-slate-600 mb-3">
            Share your link. Unlimited direct referrals. Earn ₹200 per direct.
          </p>
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
            <input
              data-testid="referral-link-input"
              readOnly
              value={refLink}
              className="flex-1 bg-transparent outline-none text-xs sm:text-sm text-slate-700"
            />
            <button
              data-testid="copy-referral-btn"
              onClick={copyRef}
              className="flex items-center gap-1 bg-red-700 hover:bg-red-800 text-white text-xs font-semibold px-3 py-2 rounded-md transition-colors active:scale-95"
            >
              <Copy size={14} /> Copy
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
            <span>Your Sponsor ID: <b className="text-slate-900">{u.user_code}</b></span>
            <Link
              data-testid="view-referrals-link"
              to="/referrals"
              className="text-red-700 font-semibold hover:underline flex items-center gap-1"
            >
              View my {team.direct} direct <ArrowUpRight size={14} />
            </Link>
          </div>
        </section>

        {/* Wallet */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <h3 className="text-center text-red-700 font-bold mb-4 flex items-center justify-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            <Wallet size={18} /> Wallet
          </h3>

          <GradientCard
            testId="card-total-earnings"
            gradient="bg-gradient-to-br from-emerald-500 to-teal-700"
            icon={CircleDollarSign}
            label="Total Earnings"
            value={INR(w.total_earnings)}
            sub="Combined income from all sources"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <GradientCard
              testId="card-direct-income"
              gradient="bg-gradient-to-br from-purple-500 to-indigo-700"
              icon={Users2}
              label="Direct Income"
              value={INR(w.direct_income)}
              sub={`${team.direct} × ₹200 — tap to view`}
              onClick={() => navigate("/direct-income")}
            />
            <GradientCard
              testId="card-levels-income"
              gradient="bg-gradient-to-br from-blue-500 to-blue-800"
              icon={TrendingUp}
              label="Levels Income"
              value={INR(w.levels_income)}
              sub="Multi-level bonuses — tap to view"
              onClick={() => navigate("/level-income")}
            />
            <GradientCard
              testId="card-rank-income"
              gradient="bg-gradient-to-br from-pink-500 to-rose-700"
              icon={Award}
              label="Rank Income"
              value={INR(w.rank_income)}
              sub="Rank achievements — tap to view"
              onClick={() => navigate("/rank-income")}
            />
            <GradientCard
              testId="card-rewards"
              gradient="bg-gradient-to-br from-orange-400 to-amber-600"
              icon={Gift}
              label="Level Upgrade Rewards"
              value={INR(w.rewards)}
              sub="Milestone rewards — tap to view"
              onClick={() => navigate("/rewards")}
            />
          </div>
        </section>

        {/* Wallet Overview */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <h3 className="text-center text-red-700 font-bold mb-4 flex items-center justify-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            <Wallet size={18} /> Wallet Overview
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <GradientCard
              testId="card-main-wallet"
              gradient="bg-gradient-to-br from-violet-600 to-purple-800"
              icon={Wallet}
              label="Main Wallet"
              value={INR(w.main_wallet)}
              sub="Primary Balance"
            />
            <GradientCard
              testId="card-e-wallet"
              gradient="bg-gradient-to-br from-teal-500 to-emerald-700"
              icon={CircleDollarSign}
              label="E-Wallet"
              value={INR(w.e_wallet)}
              sub="Digital Balance"
            />
          </div>
          <div className="mt-3">
            <GradientCard
              testId="card-bank-withdrawal"
              gradient="bg-gradient-to-br from-amber-500 to-orange-700"
              icon={Landmark}
              label="Bank Withdrawal"
              value={INR(w.bank_withdrawal)}
              sub="Withdrawn to Bank"
            />
          </div>
        </section>

        {/* Insurance Benefits */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <h3 className="text-center text-red-700 font-bold mb-3 flex items-center justify-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            <ShieldCheck size={18} /> Insurance Benefits
          </h3>
          <div className="text-center text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
            Achieve <span className="font-bold text-red-700">Motivator</span> rank to unlock insurance benefits.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { title: "Medical Insurance", desc: "Health coverage by rank", testId: "insurance-medical", to: "/insurance/medical" },
              { title: "Accidental Insurance", desc: "Accident coverage by rank", testId: "insurance-accidental", to: "/insurance/accidental" },
            ].map((it) => (
              <Link
                key={it.title}
                to={it.to}
                data-testid={it.testId}
                className="relative rounded-xl border border-slate-200 bg-slate-50 p-4 overflow-hidden hover:shadow-md hover:border-red-200 transition"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-800">{it.title}</div>
                    <div className="text-xs text-slate-500">{it.desc}</div>
                  </div>
                  <Lock size={20} className="text-slate-400" />
                </div>
                <div className="mt-3 text-xs text-slate-600">
                  Requires 5 Active Direct Referrals
                  <div className="mt-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-600 transition-all"
                      style={{ width: `${Math.min(100, (team.direct_active / 5) * 100)}%` }}
                    />
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">{team.direct_active}/5 completed</div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* News & Announcements */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <h3 className="text-center text-red-700 font-bold mb-4 flex items-center justify-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            <Newspaper size={18} /> News & Announcements
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {news.map((n) => (
              <article
                key={n.id}
                data-testid={`news-${n.id}`}
                className="rounded-xl overflow-hidden border border-slate-200 bg-white hover:shadow-md transition-shadow"
              >
                <img src={n.image} alt={n.title} className="w-full h-32 object-cover" />
                <div className="p-3">
                  <div className="font-bold text-slate-900">{n.title}</div>
                  <div className="text-xs text-slate-600 mt-1">{n.subtitle}</div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-slate-500">{n.date}</span>
                    <button className="text-red-700 font-semibold hover:underline">View Details</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {u.user_code === "UNE26000001" && <AdminLoginPanel />}
      </main>
    </div>
  );
}


function AdminLoginPanel() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(identifier.trim(), password);
      if (u.role !== "admin" && u.role !== "super_admin") {
        setError("This account does not have admin access.");
        return;
      }
      toast.success("Welcome, Admin");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="admin-login-panel"
      className="bg-white rounded-2xl shadow-sm border border-amber-300 overflow-hidden"
    >
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-5 py-3 flex items-center gap-2">
        <ShieldCheck size={18} />
        <div className="font-extrabold" style={{ fontFamily: "Outfit, sans-serif" }}>
          Admin Panel Login
        </div>
      </div>
      <form onSubmit={submit} className="p-5 space-y-3">
        <p className="text-xs text-slate-600">
          Enter admin User ID and password to open the admin panel.
        </p>
        <div>
          <label className="text-xs font-semibold text-slate-600">User ID</label>
          <input
            data-testid="dashboard-admin-id"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="e.g. Uniquedeal"
            className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-amber-500 outline-none"
            required
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Password</label>
          <div className="relative mt-1">
            <input
              data-testid="dashboard-admin-password"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-amber-500 outline-none"
              required
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800"
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        {error && (
          <div
            data-testid="dashboard-admin-error"
            className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs font-semibold px-3 py-2"
          >
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="dashboard-admin-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-bold rounded-lg py-2.5 text-sm"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
          {busy ? "Signing in…" : "Login as Admin"}
        </button>
      </form>
    </section>
  );
}
